import { useCallback, useEffect, useMemo, useState } from "react";
import { filterVisibleClasses } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
// #2852 — the "needs placings" predicate now lives in src/util/classNeedsPlacings.js,
// shared with ClassPlacings' deriveClassSummary so every rendering of the
// tag (row badge, count chip, nav badge, placings picker chip/filter)
// derives from ONE documented formula. Its contract takes each ride's
// JUDGED status (this hook's batchRideStatus-derived `derivedStatus`, with
// the raw stored status as fallback — a stored SCRATCHED always wins in
// score-common's deriveRideStatus anyway, so the two agree on scratches).
import { classNeedsPlacings } from "../util/classNeedsPlacings";

function toJudgedRide(r) {
  return { status: r.derivedStatus ?? r.status ?? null, placing: r.placing ?? null };
}

// #340/#862 — the single source of truth for "classes needing placings",
// extracted out of ClassManager so the Classes-page chip (#340,
// data-testid=needs-placings-count) and the Placings nav badge (#862) derive
// from the exact same fetch + formula and can never disagree. Mirrors the
// original #340 fetch: one showId-scoped GET /ride call covers every class
// (mirrors the announcer's #637 running-order pattern — don't fan out a call
// per class), then one batched POST /ride/status-batch resolves each ride's
// derived status. Restricted to classes with a ring + scheduledTime, since
// #591 already hides any without those from the list the chip/badge stand in
// for.
//
// #2121 — AdminShell mounts this on the VERY FIRST authenticated screen
// alongside the show load, so the two independent reads below (the class
// list and the show's rides) run concurrently rather than one-after-another:
// neither one's result feeds the other's request, only the THIRD step
// (batchRideStatus) genuinely depends on the rides fetch, so it's the only
// one left sequential.
//
// #2261 — also exposes the grouped `ridesByClassId` itself (additive
// contract change; AdminShell's #862 nav badge keeps consuming only
// needsPlacingsCount). ClassManager already mounts this hook, so its
// test-disagreement tooltip (#2252) derives the ride-side test slugs from
// this ONE show-scoped fetch instead of re-firing GET /ride?classId= per
// flagged class — and the tooltip can never go stale against a refetch,
// because there is no second cache layer anymore.
export function useClassesNeedingPlacings(showId) {
  const scoreApi = useScoreApi();
  const [classes, setClasses] = useState([]);
  const [ridesByClassId, setRidesByClassId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!showId) {
      setClasses([]);
      setRidesByClassId({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [classListRes, showRidesRes] = await Promise.all([
        scoreApi.classes.listClasses(showId),
        scoreApi.rides.listRides(undefined, showId),
      ]);
      // #1117 -- a hidden class never counts as needing placings.
      const classList = filterVisibleClasses(unwrap(classListRes));
      const showRides = unwrap(showRidesRes);
      const ids = [...new Set(showRides.map((r) => r.id).filter(Boolean))];
      const statusRows = ids.length
        ? unwrap(await scoreApi.rides.batchRideStatus({ rideIds: ids }))
        : [];
      const statusById = Object.fromEntries(statusRows.map((r) => [r.rideId, r.status]));
      const byClass = {};
      for (const r of showRides) {
        if (!r.classId) continue;
        (byClass[r.classId] ??= []).push({ ...r, derivedStatus: statusById[r.id] ?? null });
      }
      setClasses(classList);
      setRidesByClassId(byClass);
    } catch (err) {
      setClasses([]);
      setRidesByClassId({});
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showId, scoreApi]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const classesNeedingPlacings = useMemo(() => {
    const set = new Set();
    for (const c of classes) {
      if (!c.ring || !c.scheduledTime) continue;
      if (classNeedsPlacings((ridesByClassId[c.id] ?? []).map(toJudgedRide))) set.add(c.id);
    }
    return set;
  }, [classes, ridesByClassId]);

  return {
    classesNeedingPlacings,
    needsPlacingsCount: classesNeedingPlacings.size,
    ridesByClassId,
    loading,
    error,
    refetch,
  };
}

export default useClassesNeedingPlacings;
